/**
 * AP export flow (audit F2 of db358c70): "export the current selection" and "download batch X" are separate.
 *
 *  - An export INTENT is a fixed (sorted) set of expense ids plus a client token. A token is reused only to retry the
 *    SAME selection whose creation outcome is unknown (network error); any other selection gets a new token. The server
 *    also refuses a known token with a different selection (FINANCE_TOKEN_MISMATCH, migration 979).
 *  - Once the server confirms a batch, the batch is "pending download" until its file downloads. A failed download keeps
 *    it pending with its own Download action, bound to its batch id; it never touches the selection.
 *  - Only the ids of the batch that was actually created are removed from the selection.
 */

export interface ExportIntent {
  ids: string[];
  token: string;
}

export interface PendingDownload {
  batchId: string;
  bcBatchId: string;
  fileName: string;
  ids: string[];
}

export interface ApExportState {
  /** creation attempted, outcome unknown (e.g. network error): retrying THIS selection reuses its token */
  unconfirmed: ExportIntent | null;
  /** created batches whose file has not downloaded yet */
  pending: PendingDownload[];
}

export interface CreateResult {
  batch_id: string;
  bc_batch_id: string;
  batch_no: string;
  row_count: number;
  total_amount: number;
  file_name: string;
  file_sha256: string;
  replayed: boolean;
  /** first / last CRM invoice number of the batch (migration 982); absent or null for older templates */
  bc_document_first?: string | null;
  bc_document_last?: string | null;
}

/** " · invoices CRM-000001–CRM-000003" (or one number), empty when the batch has no CRM numbers */
export function documentSummary(c: CreateResult): string {
  if (!c.bc_document_first) {
    return "";
  }
  return c.bc_document_last && c.bc_document_last !== c.bc_document_first
    ? ` · invoices ${c.bc_document_first}–${c.bc_document_last}`
    : ` · invoice ${c.bc_document_first}`;
}

export interface ApExportDeps {
  create: (ids: string[], token: string) => Promise<CreateResult>;
  download: (batchId: string) => Promise<void>;
  newToken: () => string;
  describeError: (e: unknown) => string;
}

export interface FlowOutcome {
  state: ApExportState;
  /** ids to drop from the on-screen selection (only those the server put in a batch) */
  exportedIds: string[];
  message: string | null;
  error: string | null;
}

export const emptyApExportState = (): ApExportState => ({
  unconfirmed: null,
  pending: [],
});

const sorted = (ids: string[]) => Array.from(new Set(ids)).sort();
const same = (a: string[], b: string[]) =>
  a.length === b.length && a.every((x, i) => x === b[i]);

export function intentFor(
  state: ApExportState,
  selection: string[],
  newToken: () => string,
): ExportIntent {
  const ids = sorted(selection);
  if (state.unconfirmed && same(state.unconfirmed.ids, ids)) {
    return state.unconfirmed;
  }
  return { ids, token: newToken() };
}

export async function exportSelection(
  deps: ApExportDeps,
  state: ApExportState,
  selection: string[],
): Promise<FlowOutcome> {
  if (selection.length === 0) {
    return {
      state,
      exportedIds: [],
      message: null,
      error: "Select at least one expense.",
    };
  }
  const intent = intentFor(state, selection, deps.newToken);
  let created: CreateResult;
  try {
    created = await deps.create(intent.ids, intent.token);
  } catch (e) {
    return {
      // keep the intent: retrying the SAME selection is idempotent; a different selection gets a new token
      state: { ...state, unconfirmed: intent },
      exportedIds: [],
      message: null,
      error: deps.describeError(e),
    };
  }
  const p: PendingDownload = {
    batchId: created.batch_id,
    bcBatchId: created.bc_batch_id,
    fileName: created.file_name,
    ids: intent.ids,
  };
  let next: ApExportState = {
    unconfirmed: null,
    pending: [...state.pending.filter((x) => x.batchId !== p.batchId), p],
  };
  const summary = `Batch ${created.bc_batch_id} (${created.batch_no}): ${created.row_count} lines${documentSummary(created)}, file ${created.file_name}`;
  try {
    await deps.download(created.batch_id);
    next = {
      ...next,
      pending: next.pending.filter((x) => x.batchId !== p.batchId),
    };
    return {
      state: next,
      exportedIds: intent.ids,
      message: `${summary} -- downloaded.`,
      error: null,
    };
  } catch (e) {
    return {
      state: next,
      exportedIds: intent.ids,
      message: null,
      error: `${summary} was CREATED but its file did not download (${deps.describeError(e)}). Use its Download button; do not export it again.`,
    };
  }
}

export async function retryDownload(
  deps: ApExportDeps,
  state: ApExportState,
  batchId: string,
): Promise<FlowOutcome> {
  const p = state.pending.find((x) => x.batchId === batchId);
  if (!p) {
    return {
      state,
      exportedIds: [],
      message: null,
      error: "That batch is not waiting for a download.",
    };
  }
  try {
    await deps.download(batchId);
    return {
      state: {
        ...state,
        pending: state.pending.filter((x) => x.batchId !== batchId),
      },
      exportedIds: [],
      message: `Batch ${p.bcBatchId} downloaded (${p.fileName}).`,
      error: null,
    };
  } catch (e) {
    return {
      state,
      exportedIds: [],
      message: null,
      error: `Batch ${p.bcBatchId} still did not download: ${deps.describeError(e)}`,
    };
  }
}
